import { ChangeDetectionStrategy, Component, OnDestroy, input, signal } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';

/**
 * Referencia de soporte del contrato de errores (`trace_id`).
 *
 * Componente tonto: solo recibe el identificador y lo muestra con su etiqueta
 * traducida y un control para copiarlo. Es un valor opaco y aleatorio que el
 * backend genera por petición; no lleva datos del usuario ni de la operación
 * (contrato §12.3), y aquí no se registra en ningún log.
 */
@Component({
  selector: 'app-error-trace-id',
  standalone: true,
  imports: [TranslocoModule],
  templateUrl: './error-trace-id.component.html',
  styleUrl: './error-trace-id.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ErrorTraceIdComponent implements OnDestroy {
  readonly traceId = input.required<string>();

  readonly copied = signal(false);

  private copiedTimer: ReturnType<typeof setTimeout> | null = null;

  copy(): void {
    const value = this.traceId();
    if (!value) return;
    const onCopied = () => this.flagCopied();
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(value).then(onCopied).catch(() => {
        if (this.legacyCopy(value)) onCopied();
      });
    } else if (this.legacyCopy(value)) {
      onCopied();
    }
  }

  ngOnDestroy(): void {
    this.clearTimer();
  }

  private flagCopied(): void {
    this.clearTimer();
    this.copied.set(true);
    this.copiedTimer = setTimeout(() => {
      this.copied.set(false);
      this.copiedTimer = null;
    }, 2000);
  }

  private clearTimer(): void {
    if (this.copiedTimer !== null) {
      clearTimeout(this.copiedTimer);
      this.copiedTimer = null;
    }
  }

  /** Fallback para contextos no-seguros donde `navigator.clipboard` no existe. */
  private legacyCopy(value: string): boolean {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  }
}
